import React from 'react';
import { Link } from 'react-router-dom';
import { writeEventDayTab, type EventDayTab } from '../../lib/event-day';
import { bigScreenPath } from '../../lib/big-screen';
import { scoringCheckInCallerPath, scoringCheckInPath, scoringListPath, scoringRound1TeamsPath } from '../../lib/scoring-routes';

export type EventDayPlace = EventDayTab | 'teams';

export interface EventDayCounts {
  checkedIn: number | null;
  total: number;
  shootingNow: number | null;
  scoresEntered: number;
  waiting: number | null;
}

const tabClass = (current: boolean) =>
  `inline-flex min-h-14 items-center justify-center rounded-[var(--radius-md)] px-3 text-base font-semibold ${
    current
      ? 'bg-[var(--color-accent)] text-white'
      : 'border border-[var(--color-border-strong)] bg-[var(--color-surface-1)] text-[var(--color-text-primary)]'
  }`;

function Count({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-1)] px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-text-tertiary)]">{label}</p>
      <p className="mt-0.5 text-2xl font-semibold tabular-nums text-[var(--color-text-primary)]">{value}</p>
    </div>
  );
}

function showCount(value: number | null): string {
  return value == null ? '–' : String(value);
}

/** Tabs, live counts, and the event-day switch shared by the rifle scoring screens. */
export function EventDayBar({
  slug,
  place,
  isAdmin,
  eventDay,
  toggleBusy,
  onToggle,
  counts,
  toggleError,
}: {
  slug: string;
  place: EventDayPlace;
  isAdmin: boolean;
  eventDay: boolean;
  toggleBusy: boolean;
  onToggle: () => void;
  counts: EventDayCounts;
  toggleError?: string | null;
}) {
  return (
    <section aria-label="Event day" className="space-y-3">
      {isAdmin ? (
        <button
          type="button"
          role="switch"
          aria-checked={eventDay}
          disabled={toggleBusy}
          onClick={onToggle}
          className="flex min-h-14 w-full items-center justify-between rounded-[var(--radius-md)] border border-[var(--color-border-strong)] bg-[var(--color-surface-1)] px-4 text-left text-base font-semibold text-[var(--color-text-primary)] disabled:opacity-60"
        >
          <span>Event day</span>
          <span className={eventDay ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-tertiary)]'}>
            {toggleBusy ? 'Saving…' : eventDay ? 'On' : 'Off'}
          </span>
        </button>
      ) : null}
      {toggleError ? (
        <p className="text-sm text-[var(--color-error-text)]" role="alert">
          {toggleError}
        </p>
      ) : null}
      {isAdmin ? (
        <div className="grid grid-cols-2 gap-2" aria-label="Event day counts">
          <Count label="Checked in" value={counts.checkedIn == null ? '–' : `${counts.checkedIn}/${counts.total}`} />
          <Count label="Shooting now" value={showCount(counts.shootingNow)} />
          <Count label="Scores entered" value={`${counts.scoresEntered}/${counts.total}`} />
          <Count label="Still waiting" value={showCount(counts.waiting)} />
        </div>
      ) : null}
      <nav aria-label="Event day jobs" className="grid grid-cols-2 gap-2">
        {isAdmin ? (
          <Link
            to={scoringCheckInPath(slug)}
            onClick={() => writeEventDayTab(slug, 'check-in')}
            aria-current={place === 'check-in' ? 'page' : undefined}
            className={tabClass(place === 'check-in')}
          >
            Check in
          </Link>
        ) : null}
        {isAdmin ? (
          <Link
            to={scoringCheckInCallerPath(slug)}
            onClick={() => writeEventDayTab(slug, 'caller')}
            aria-current={place === 'caller' ? 'page' : undefined}
            className={tabClass(place === 'caller')}
          >
            Caller
          </Link>
        ) : null}
        <Link
          to={scoringListPath(slug)}
          onClick={() => writeEventDayTab(slug, 'scores')}
          aria-current={place === 'scores' ? 'page' : undefined}
          className={tabClass(place === 'scores')}
        >
          Scores
        </Link>
        <a href={bigScreenPath(slug)} target="_blank" rel="noreferrer" className={tabClass(false)}>
          Big screen
        </a>
        {isAdmin ? (
          <Link
            to={scoringRound1TeamsPath(slug)}
            aria-current={place === 'teams' ? 'page' : undefined}
            className={tabClass(place === 'teams')}
          >
            Teams
          </Link>
        ) : null}
      </nav>
    </section>
  );
}

/** Setup tools stay on the page. Event day tucks them into a collapsed section. */
export function EventDayMore({ enabled, children }: { enabled: boolean; children: React.ReactNode }) {
  if (!enabled) return <>{children}</>;
  return (
    <details id="event-day-more" className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-1)]">
      <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between px-4 text-base font-semibold text-[var(--color-text-primary)] [&::-webkit-details-marker]:hidden">
        More
        <span aria-hidden="true" className="text-[var(--color-text-tertiary)]">
          ▾
        </span>
      </summary>
      <div className="space-y-4 border-t border-[var(--color-border)] px-4 py-4">{children}</div>
    </details>
  );
}
